import { Link } from 'react-router-dom';

import { getSource } from '@/sources/registry';
import { cn } from '@/lib/utils';

import type { DedupGroup } from './useDedup';
import { sourceBadge } from './sourceBadge';

export interface MatchGroupProps {
  group: DedupGroup;
  className?: string;
}

/**
 * Renders a single cross-source dedup group: the canonical title plus a
 * horizontal row of source-tagged thumbnails, each linking to that
 * source's gallery page.
 *
 * Visual intent is *advisory* — we never auto-merge. A user who likes
 * three different versions of the same comic should be able to keep all
 * three favorites.
 */
export function MatchGroup({ group, className }: MatchGroupProps) {
  return (
    <article
      className={cn(
        'flex flex-col gap-2 rounded-lg border border-border/40 bg-card p-3',
        className,
      )}
    >
      <header className="flex items-center justify-between gap-2">
        <h4 className="line-clamp-1 text-sm font-medium">{group.canonicalTitle}</h4>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {group.items.length} sources
        </span>
      </header>
      <div className="flex flex-wrap gap-2">
        {group.items.map((item) => (
          <Link
            key={`${item.sourceId}|${item.comicId}`}
            to={`/source/${item.sourceId}/comic/${encodeURIComponent(item.comicId)}`}
            className="group relative flex w-20 flex-shrink-0 flex-col gap-1"
            title={item.title}
          >
            <div className="aspect-[3/4] w-full overflow-hidden rounded bg-muted">
              {item.cover ? (
                <img
                  src={item.cover}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
                />
              ) : null}
            </div>
            <span className="line-clamp-1 text-center text-[10px] uppercase tracking-wide text-muted-foreground">
              {sourceBadge(getSource(item.sourceId)?.id ?? item.sourceId)}
            </span>
          </Link>
        ))}
      </div>
    </article>
  );
}
